import { MODES, TABLE } from './constants';
import { GameConstructorParams, TSize } from './types';
import { HallmapTable } from 'entities/hallmap';
import { Layer } from 'konva/lib/Layer';
import Table from './table/table';
import Zone from './zone';
import Controls from './controls';
import { Stage } from 'konva/lib/Stage';
import Konva from 'konva';
import { ImageConfig } from 'konva/lib/shapes/Image';

type StringKeysAndValues = {
	[key: string]: string;
};

const loadImages = () => {
	const urls: StringKeysAndValues = {
		iconClock: '/hallmap/icons/natural.png',
		iconTimer: '/hallmap/icons/timer.svg',
		iconPerson: '/hallmap/icons/person.svg',
	};

	let assets: any = {};

	for (const key in urls) {
		let imageObj = new Image();
		imageObj.src = urls[key];

		assets[key] = imageObj;
	}

	return assets;
};

export const assets = loadImages();
// export const stageControls = new Controls(stage);
export class Game {
	resources: Table[] = [];
	resourcesMap = new Map();
	resourcesMapByName = new Map();
	zones: Zone[] = [];
	onReady: ((game: Game) => void) | undefined;
	onClickTable: ((table: Table) => void) | undefined;
	onDblClickTable: ((table: Table) => void) | undefined;
	onMoveTable: ((table: Table) => void) | undefined;
	onBeforeSelectTable?: () => void;
	onSelectTable: ((table: Table, selected: boolean, manual: boolean) => void) | undefined;
	onDeselectTable: ((table: Table) => void) | undefined;
	onConflictTable: ((table: Table, resolved: boolean) => void) | undefined;
	private mode = '';
	private backgroundImage: any;
	private controls: Controls;
	hasTableFirstClick = false;
	private disableTableClick = false;

	private layer: Layer | undefined;
	private stage: Stage;
	private tables: HallmapTable[] = [];

	constructor({
		onReady,
		onMoveTable,
		onClickTable,
		onDblClickTable,
		onBeforeSelectTable,
		onSelectTable,
		onDeselectTable,
		onConflictTable,
		stage,
		tables,
	}: GameConstructorParams) {
		this.controls = new Controls(stage);

		if (onReady) {
			this.onReady = onReady;
		}

		if (onClickTable) {
			this.onClickTable = onClickTable;
		}

		if (onDblClickTable) {
			this.onDblClickTable = onDblClickTable;
		}

		if (onMoveTable) {
			this.onMoveTable = onMoveTable;
		}

		if (onSelectTable) {
			this.onSelectTable = onSelectTable;
		}

		if (onBeforeSelectTable) {
			this.onBeforeSelectTable = onBeforeSelectTable;
		}

		if (onDeselectTable) {
			this.onDeselectTable = onDeselectTable;
		}

		if (onConflictTable) {
			this.onConflictTable = onConflictTable;
		}

		this.stage = stage;
		this.tables = tables;
		this.init();
		this.stage.on('wheel', (e: any) => this.controls.scaling(e));
		this.controls.initialize(this.resources);
		this.setMode(MODES.EDITOR);
		const window2: any = window;
		window2.game = this;
	}

	changeStageByY(coord: number) {
		this.stage.y(coord);
	}

	getStageContent() {
		return this.stage.content;
	}

	findTablesByPlace(place: number) {
		return this.resourcesMap.get(place);
	}

	toggleDisableTableClick(isDisabled: boolean) {
		this.resources.forEach(table => {
			table.setDisableClick(isDisabled);
		})

		this.disableTableClick = isDisabled;
	}

	focusOnTablesByPlace(place: number) {
		const places = this.findTablesByPlace(place);
		if (places) {
			this.focusOnTables(places);
		}
	}

	getTablesBounds(resources: Table[]) {
		let minX = Infinity,
			minY = Infinity,
			maxX = 0,
			maxY = 0;

		// Перебор всех столов для вычисления границ
		resources.forEach(table => {
			const tableElement = table.element;
			minX = Math.min(minX, tableElement.x());
			minY = Math.min(minY, tableElement.y());
			maxX = Math.max(
				maxX,
				tableElement.x() + table.shape.width() * this.stage.scaleX(),
			);
			maxY = Math.max(
				maxY,
				tableElement.y() + table.shape.height() * this.stage.scaleY(),
			);
		});

		return { minX, minY, maxX, maxY };
	}

	focusOnTables(resources: Table[]) {
		const bounds = this.getTablesBounds(resources);
		const width = bounds.maxX - bounds.minX;
		const height = bounds.maxY - bounds.minY;

		const scaleX = this.stage.width() / width;
		const scaleY = this.stage.height() / height;

		const scale = Math.min(scaleX, scaleY) * 0.7;
		this.stage.scale({ x: scale, y: scale });

		this.stage.position({
			x: this.stage.width() / 2 - (bounds.minX + width / 2) * scale,
			y: this.stage.height() / 2 - (bounds.minY + height / 2) * scale,
		});

		if (this.layer) {
			this.layer.batchDraw();
		}
	}

	setBg(image: typeof Image) {
		if (this.backgroundImage) {
			this.backgroundImage.image(image);
		}
	}

	setScale(factor: number) {
		this.controls.setScale(factor);
	}

	setMode(state: string) {
		this.mode = state;
		this.deselect();

		if (state == MODES.STATIC) {
			this.resources.forEach(table => {
				table.draggable = false;
				if (!table.isActive) {
					table.setActive(false);
				}
				table.canSelect = false;
				table.setStatus(table.status);
				// table.setBooked(table.options.booked);
			});
			this.zones.forEach(zone => zone.show());
		}
		if (state == MODES.EDITOR) {
			this.resources.forEach(table => {
				table.draggable = true;
				if (!table.isActive) {
					table.setActive(false);
				} else {
					table.setActiveAppearence();
				}
				table.canSelect = false;
			});
			this.zones.forEach(zone => zone.show());
		}
		if (state == MODES.SELECT) {
			this.resources.forEach(table => {
				table.draggable = false;
				table.setActiveAppearence();
				if (table.isActive == false) {
					table.setInactiveAppearence();
				}

				if (table.isSelectable == false) {
					table.setInactiveAppearence();
				}

				table.canSelect = true;
			});
			this.zones.forEach(zone => zone.hide());
		}
	}

	selectResources(resources: Table[]) {
		for (let i = 0; i < resources.length; i++) {
			resources[i].select();
		}
	}

	deselect() {
		for (let i = 0; i < this.resources.length; i++) {
			this.resources[i].deselect();
		}
	}

	addZone(tables: Table[], color: string, title: string) {
		const zone = new Zone(tables, color, title);
		this.zones.push(zone);

		return zone;
	}

	removeZones(ids: number[]) {
		const selected = this.zones.filter(z => {
			return ids.find(zoneId => zoneId == z.id);
		});
		selected.forEach(selectedZone => selectedZone.remove());
		this.zones = this.zones.filter(z => !selected.includes(z));
	}
	addTable(options = {}) {
		const table = new Table(options, this.controls);
		this.resources.push(table);
		if (this.layer) {
			this.layer.add(table.element);
		}
		this.attachEventsTable(table);
		this.setMode(this.mode);
		return table;
	}

	setTableFirstClick() {
		this.hasTableFirstClick = true;
	}

	private init() {
		this.createTables();

		if (this.onReady) {
			this.onReady(this);
		}
	}

	private initStage() {
		const layer = new Layer();
		this.layer = layer;
		this.stage.add(layer);

		layer.draw();
	}

	private initBg() {
		const config: ImageConfig = {
			x: 0,
			y: 0,
			image: undefined,
		};
		const bg = new Konva.Image(config);
		this.backgroundImage = bg;
		if (this.layer) {
			this.layer.add(bg);
		}
	}

	private createTables() {
		this.initStage();
		this.initBg();

		this.tables.map(table => {
			const form = table.resourceType.form;
			const capacity = table.resourceType.capacity;
			const name = table.resource.name;
			const id = table.resource.id;
			const size: TSize = table.resourceType.size;
			const layout = table.layout;

			const isActive = table.resource.is_active;
			const place = table.resource.place;

			let coord: { x: number; y: number } = {
				x: 0,
				y: 0,
			};

			if (table.config && table.config.x) {
				coord.x = table.config.x;
			}

			if (table.config && table.config.y) {
				coord.y = table.config.y;
			}

			const tableSizes = TABLE.SIZES;

			if (coord.x || coord.y) {
				const newTable = this.addTable({
					id,
					form,
					capacity,
					name,
					size: tableSizes[size],
					booked: 0,
					config: table,
					layout,
				});

				newTable.onClick = (table: Table) => {
					if (this.disableTableClick) {
						return;
					}

					if (this.onClickTable) {
						this.onClickTable(table);
					}
				};

				newTable.onDblClick = (table: Table) => {
					if (this.onDblClickTable) {
						this.onDblClickTable(table);
					}
				};

				newTable.onUpdate = (table: Table) => {
					if (this.onMoveTable) {
						this.onMoveTable(table);
					}
				};

				newTable.onSelect = (table: Table, selected: boolean, manual: boolean) => {
					if (this.disableTableClick) {
						return;
					}

					if (this.onSelectTable) {
						this.onSelectTable(table, selected, manual);
					}
				};

				newTable.onBeforeSelect = () => {
					if (this.onBeforeSelectTable) {
						this.onBeforeSelectTable();
					}
				};

				newTable.onDeselectTable = (table: Table) => {
					if (this.onDeselectTable) {
						this.onDeselectTable(table);
					}
				};

				newTable.onConflictTable = (
					table: Table,
					resolved: boolean,
				) => {
					if (this.onConflictTable) {
						this.onConflictTable(table, resolved);
					}
				};

				newTable.element.x(coord.x);
				newTable.element.y(coord.y);

				if (table.config && table.config.rotation) {
					newTable.rotate(table.config.rotation)
				}

				newTable.isActive = isActive;

				this.resourcesMapByName.set(name, newTable);

				const tables = this.resourcesMap.get(place);
				if (tables) {
					this.resourcesMap.set(place, [newTable, ...tables]);
				} else {
					this.resourcesMap.set(place, [newTable]);
				}
			}
		});
	}

	private attachEventsTable(table: Table) {
		if (window.matchMedia("(pointer: coarse)").matches) {
			table.element.on('touchstart', () => {
				if (this.mode == MODES.SELECT) {
					if (!table.isSelectable || !table.isActive) {
						table.deselect();
						table.setInactiveAppearence();
					}
				}
			});
		} else {
			table.element.on('click', () => {
				if (this.mode == MODES.SELECT) {
					if (!table.isSelectable || !table.isActive) {
						table.deselect();
						table.setInactiveAppearence();
					}
				}
			});
		}
	}
}

export default Game;
